/**
 * @module components/AmbientMode/scenes/ActivityScene
 * @description Escena de actividad reciente para el modo ambient (20 segundos por rotación).
 *
 * Muestra un feed en tiempo real de las últimas acciones del sistema con:
 * - Iniciales del actor (máx. 2 letras o "SIS" para sistema) — NUNCA nombre completo
 * - Tipo de acción con ícono y color según criticidad
 * - Timestamp relativo ("hace 5 min")
 * - Barra de impacto si hubo cambio de cumplimiento
 *
 * ## Privacidad
 * `SafeAuditEntry.actorInitials` garantiza que esta escena puede mostrarse
 * en espacios públicos sin revelar nombres de usuarios. Ver `types/ambient.ts`.
 *
 * @see SafeAuditEntry en types/ambient.ts para el diseño de privacidad
 * @see SceneEngine que monta esta escena durante 20s
 */
import React, { memo, useEffect, useMemo, useRef, useState } from 'react';
import { useData } from '../../../contexts/DataContext';
import type { SafeAuditEntry } from '../../../types/ambient';

// ─── Criticality helpers ──────────────────────────────────────────────────────

function deriveCriticality(
  actionType: string,
  impactChange?: number
): SafeAuditEntry['criticality'] {
  if (actionType === 'status_change' && impactChange !== undefined && impactChange < 0) return 'critical';
  if (actionType === 'status_change') return 'high';
  if (actionType === 'assignment')    return 'medium';
  if (actionType === 'task_completed') return 'medium';
  if (actionType === 'evidence_upload') return 'low';
  if (actionType === 'due_date_change') return 'medium';
  if (actionType === 'task_created')    return 'low';
  return 'low';
}

const CRITICALITY_CONFIG: Record<SafeAuditEntry['criticality'], { label: string; border: string; badge: string }> = {
  critical: { label: 'CRÍTICO', border: 'border-red-500',    badge: 'bg-red-500/20 text-red-400'     },
  high:     { label: 'ALTO',    border: 'border-orange-500', badge: 'bg-orange-500/20 text-orange-400' },
  medium:   { label: 'MEDIO',   border: 'border-blue-500',   badge: 'bg-blue-500/20 text-blue-400'   },
  low:      { label: 'BAJO',    border: 'border-gray-600',   badge: 'bg-gray-700 text-gray-400'      },
};

function formatRelativeTime(timestamp: string): string {
  const diff = Date.now() - new Date(timestamp).getTime();
  const minutes = Math.floor(diff / 60_000);
  const hours   = Math.floor(diff / 3_600_000);
  const days    = Math.floor(diff / 86_400_000);
  if (days > 0)    return `hace ${days}d`;
  if (hours > 0)   return `hace ${hours}h`;
  if (minutes > 0) return `hace ${minutes}m`;
  return 'recién';
}

// ─── Anonymize user ───────────────────────────────────────────────────────────

function toInitials(name?: string): string {
  if (!name) return 'SIS';
  const parts = name.trim().split(/\s+/);
  return parts
    .map(p => p[0] ?? '')
    .join('')
    .slice(0, 2)
    .toUpperCase() || 'SIS';
}

// ─── Entry card ───────────────────────────────────────────────────────────────

function EntryCard({ entry, visible }: { entry: SafeAuditEntry; visible: boolean }) {
  const cfg = CRITICALITY_CONFIG[entry.criticality];

  return (
    <div
      className={`flex items-center gap-4 py-4 px-6 border-l-4 ${cfg.border}
        bg-gray-900/50 rounded-r-xl
        transition-all duration-500
        ${visible ? 'opacity-100 translate-x-0' : 'opacity-0 -translate-x-4'}`}
    >
      {/* Avatar de iniciales */}
      <div className="w-12 h-12 rounded-full bg-gray-700 border border-gray-600 flex items-center justify-center shrink-0">
        <span className="text-sm font-bold text-gray-300">{entry.actorInitials}</span>
      </div>

      {/* Texto */}
      <div className="flex-1 min-w-0">
        <p className="text-base text-gray-200 truncate">{entry.action}</p>
        <p className="text-sm text-gray-500 mt-1">{formatRelativeTime(entry.timestamp)}</p>
      </div>

      {/* Badge de criticidad */}
      <span className={`shrink-0 text-xs font-bold px-3 py-1 rounded ${cfg.badge}`}>
        {cfg.label}
      </span>
    </div>
  );
}

// ─── Main scene ──────────────────────────────────────────────────────────────

export const ActivityScene = memo(function ActivityScene() {
  const { filteredControls } = useData();
  const scrollRef = useRef<HTMLDivElement>(null);
  const [visibleCount, setVisibleCount] = useState(0);

  // Preparar entradas anonimizadas (memoizado)
  const entries = useMemo((): SafeAuditEntry[] => {
    const raw = filteredControls
      .flatMap(c =>
        (c.auditTrail ?? []).map(entry => ({ ...entry, _controlPriority: c.priority }))
      )
      .sort((a, b) => new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime())
      .slice(0, 20);

    return raw.map(entry => ({
      id: entry.id,
      action: entry.action,
      actionType: entry.actionType,
      actorInitials: toInitials((entry.user as { name?: string })?.name),
      timestamp: entry.timestamp,
      criticality: deriveCriticality(
        entry.actionType,
        (entry.impactMetrics as { completionChange?: number } | undefined)?.completionChange
      ),
      impactChange: (entry.impactMetrics as { completionChange?: number } | undefined)?.completionChange,
    }));
  }, [filteredControls]);

  // Aparición progresiva
  useEffect(() => {
    setVisibleCount(0);
    if (entries.length === 0) return;

    const interval = setInterval(() => {
      setVisibleCount(prev => {
        if (prev >= entries.length) {
          clearInterval(interval);
          return prev;
        }
        return prev + 1;
      });
    }, 900);

    return () => clearInterval(interval);
  }, [entries.length]);

  // Auto-scroll al fondo
  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTo({
        top: scrollRef.current.scrollHeight,
        behavior: 'smooth',
      });
    }
  }, [visibleCount]);

  return (
    <div className="flex flex-col h-full w-full max-w-5xl mx-auto px-8 py-10 gap-5">
      {/* Header */}
      <div className="flex items-center justify-between shrink-0">
        <h2 className="text-base uppercase tracking-widest text-gray-500">
          Actividad Reciente del Sistema
        </h2>
        <span className="text-sm text-gray-600 font-mono">
          {entries.length} eventos
        </span>
      </div>

      {/* Scrollable list */}
      <div
        ref={scrollRef}
        className="flex-1 overflow-hidden flex flex-col gap-3"
        style={{ maskImage: 'linear-gradient(to bottom, transparent 0%, black 8%, black 92%, transparent 100%)' }}
      >
        {entries.length === 0 ? (
          <p className="text-gray-600 text-sm text-center mt-8">
            Sin actividad reciente registrada.
          </p>
        ) : (
          entries.map((entry, i) => (
            <EntryCard
              key={entry.id}
              entry={entry}
              visible={i < visibleCount}
            />
          ))
        )}
      </div>

      {/* Indicador de trazabilidad */}
      <div className="flex items-center gap-2 shrink-0 pt-2 border-t border-gray-800">
        <div className="w-2 h-2 rounded-full bg-emerald-500 animate-pulse" />
        <span className="text-xs text-gray-600">
          Registro de auditoría activo — datos anonimizados para visualización pública
        </span>
      </div>
    </div>
  );
});
